import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocalize } from "../i18n";
import {
  Box,
  Dialog,
  Link,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// Index texts are Markdown-style links, e.g.
// "[NASA](https://…)". Render as links (http/https only) without HTML injection.
const LINK_PATTERN = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

function withLinks(text, newTabLabel) {
  const parts = [];
  let last = 0;
  for (const match of text.matchAll(LINK_PATTERN)) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <Link
        key={match.index}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
      >
        {match[1]}
        {/* Marks that the link opens in a new tab}. */}
        <OpenInNewIcon
          aria-label={newTabLabel}
          sx={{ fontSize: "0.85em", ml: 0.25, verticalAlign: "-0.1em" }}
        />
      </Link>,
    );
    last = match.index + match[0].length;
  }
  parts.push(text.slice(last));
  return parts;
}

export default function IndexInfo({ index }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const localize = useLocalize();

  if (!index?.id) return null;

  // Some indices use their own short name as the unit
  const unit =
    index.unit_en && index.unit_en !== index.short_name_en
      ? localize(index, "unit")
      : null;
  const description = localize(index, "description");
  const sources = localize(index, "additional_info");

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: -0.5 }}>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", flex: 1, pl: 0.5, lineHeight: 1.4 }}
        >
          {localize(index, "full_name")}
          {unit && ` (${unit})`}
        </Typography>
        <Tooltip title={t("map.aboutIndex")}>
          <IconButton
            size="small"
            onClick={() => setOpen(true)}
            aria-label={t("map.aboutNamed", {
              name: localize(index, "short_name"),
            })}
            // Tinted
            sx={{
              color: "primary.main",
              bgcolor: "rgba(var(--primary-rgb), 0.1)",
              "&:hover": { bgcolor: "rgba(var(--primary-rgb), 0.18)" },
            }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {localize(index, "short_name")}
          <Typography
            variant="body2"
            component="span"
            sx={{ color: "text.secondary", display: "block" }}
          >
            {localize(index, "full_name")}
          </Typography>
          <IconButton
            aria-label={t("common.close")}
            onClick={() => setOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {unit && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              <strong>{t("map.unit")}</strong> {unit}
            </Typography>
          )}
          {/* line takes the direction of its own first letters. */}
          {description && (
            <Typography
              variant="body2"
              dir="auto"
              sx={{ mb: 2, textAlign: "start" }}
            >
              {withLinks(description, t("map.opensNewTab"))}
            </Typography>
          )}
          {sources && (
            <>
              <Typography
                variant="overline"
                sx={{ color: "text.secondary", display: "block" }}
              >
                {t("map.dataSources")}
              </Typography>
              {/* Attribution text arrives with line breaks: one line each. */}
              <Typography
                variant="caption"
                component="div"
                sx={{ color: "text.secondary" }}
              >
                {sources.split("\n").map((line, i) => (
                  <Box
                    key={i}
                    component="p"
                    dir="auto"
                    sx={{ m: 0, minHeight: "1em", textAlign: "start" }}
                  >
                    {withLinks(line, t("map.opensNewTab"))}
                  </Box>
                ))}
              </Typography>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
